import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Can from '../../auth/Can';
import { usePermission } from '../../auth/usePermission';
import { listUsers, deleteUser, toggleUserStatus } from '../../api/users';
import { listRoles } from '../../api/roles';
import { pushToast } from '../../components/ui/toastBus';
import UserFormModal from './UserFormModal';

export default function UsersList() {
  const { can } = usePermission();
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = { search: search || undefined, role: role || undefined, status: status || undefined, page };

  const usersQuery = useQuery({ queryKey: ['users', params], queryFn: () => listUsers(params) });
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: listRoles });

  const toggleStatusMutation = useMutation({
    mutationFn: toggleUserStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      pushToast({ type: 'success', message: 'Status updated.' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      pushToast({ type: 'success', message: 'User deleted.' });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete user.' });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone', render: (row) => row.phone || '—' },
    {
      key: 'roles',
      label: 'Roles',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.roles.length > 0 ? row.roles.map((r) => <Badge key={r}>{r}</Badge>) : <span className="text-gray-400">—</span>}
        </div>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => (
        <Can perm="users.toggle-status">
          <button
            onClick={() => toggleStatusMutation.mutate(row.id)}
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              row.is_active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            {row.is_active ? 'Active' : 'Inactive'}
          </button>
        </Can>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Can perm="users.update">
            <Link to={`/users/${row.id}`} className="text-primary hover:underline">
              Edit
            </Link>
          </Can>
          <Can perm="permissions.assign">
            <Link to={`/users/${row.id}?tab=permissions`} className="text-primary hover:underline">
              Manage Access
            </Link>
          </Can>
          <Can perm="users.delete">
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Users"
        actions={
          <Can perm="users.create">
            <Button onClick={() => setAddOpen(true)}>Add User</Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          placeholder="Search name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-64"
        />
        <Select value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }} className="w-48">
          <option value="">All roles</option>
          {(rolesQuery.data || []).map((r) => (
            <option key={r.id} value={r.name}>
              {r.name}
            </option>
          ))}
        </Select>
        <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="w-40">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
      </div>

      <Table
        columns={columns}
        data={usersQuery.data?.data || []}
        isLoading={usersQuery.isLoading}
        pagination={usersQuery.data}
        onPageChange={setPage}
        emptyMessage="No users found."
      />

      {can('users.create') && <UserFormModal open={addOpen} onClose={() => setAddOpen(false)} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete user"
        message={`Delete ${deleteTarget?.name}? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
